import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import PlaceIcon from '@mui/icons-material/Place';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useCompanyAiAgents } from '../../hooks/useCompanyAiAgents';
import {
  COMPANY_PROFILE_FIELDS,
  normalizeCompanyProfile,
} from '../../lib/companyProfile';

/** @param {Record<string, string>} profile */
function emptyForm(profile) {
  const base = { name: '' };
  for (const key of COMPANY_PROFILE_FIELDS) base[key] = '';
  return { ...base, ...profile };
}

/**
 * General company information — name, contact details, and address.
 * Company Admins can edit; writes land on `companies/{companyId}`.
 * @param {{ userData?: { companyId?: string, role?: string } }} props
 */
export default function CompanyGeneralSettings({ userData }) {
  const { workspace, companyId, refresh } = useWorkspace();
  const isCompanyAdmin = userData?.role === 'company_admin';
  const resolvedCompanyId = companyId || userData?.companyId;

  const [forceTicketApp, setForceTicketApp] = useState(
    Boolean(workspace?.forceTicketAppSelection),
  );
  const [savingTicketPref, setSavingTicketPref] = useState(false);

  const { agents: rosterAgents, loading: rosterLoading } = useCompanyAiAgents(
    resolvedCompanyId,
  );

  const activeRoster = useMemo(
    () =>
      (Array.isArray(rosterAgents) ? rosterAgents : []).filter((a) => {
        const status =
          typeof a.status === 'string' ? a.status.toLowerCase() : '';
        return status === 'active' || status === 'enabled' || !status;
      }),
    [rosterAgents],
  );

  const [defaultTriageAgentId, setDefaultTriageAgentId] = useState(
    typeof workspace?.defaultTriageAgentId === 'string'
      ? workspace.defaultTriageAgentId
      : '',
  );
  const [savingTriageAgent, setSavingTriageAgent] = useState(false);
  const [triageDirty, setTriageDirty] = useState(false);

  useEffect(() => {
    setForceTicketApp(Boolean(workspace?.forceTicketAppSelection));
  }, [workspace?.forceTicketAppSelection]);

  useEffect(() => {
    if (triageDirty) return;
    setDefaultTriageAgentId(
      typeof workspace?.defaultTriageAgentId === 'string'
        ? workspace.defaultTriageAgentId
        : '',
    );
  }, [workspace?.defaultTriageAgentId, triageDirty]);

  const initial = useMemo(
    () => ({
      ...emptyForm(normalizeCompanyProfile(workspace?.profile || null)),
      name: workspace?.name || '',
    }),
    [workspace?.profile, workspace?.name],
  );

  const [form, setForm] = useState(initial);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setForm(initial);
    setDirty(false);
  }, [initial]);

  const setField = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
    setSuccess('');
  };

  const handleToggleForceTicketApp = async (event) => {
    const next = event.target.checked;
    if (!resolvedCompanyId) return;
    setForceTicketApp(next);
    setSavingTicketPref(true);
    setError('');
    setSuccess('');
    try {
      await updateDoc(doc(db, 'companies', resolvedCompanyId), {
        forceTicketAppSelection: next,
        updatedAt: serverTimestamp(),
      });
      await refresh?.();
      setSuccess(
        next
          ? 'Enabled: users must pick an App/Site when creating tickets.'
          : 'Disabled: tickets can be created as global workspace items.',
      );
    } catch (e) {
      console.error('save forceTicketAppSelection:', e);
      setForceTicketApp(!next);
      setError(e.message || 'Could not update ticketing preference.');
    } finally {
      setSavingTicketPref(false);
    }
  };

  const handleSaveTriageAgent = async () => {
    if (!resolvedCompanyId) return;
    setSavingTriageAgent(true);
    setError('');
    setSuccess('');
    try {
      await updateDoc(doc(db, 'companies', resolvedCompanyId), {
        defaultTriageAgentId: defaultTriageAgentId || null,
        updatedAt: serverTimestamp(),
      });
      await refresh?.();
      setTriageDirty(false);
      setSuccess(
        defaultTriageAgentId
          ? 'Default triage agent saved for the global support widget.'
          : 'Default triage agent cleared. The widget will fall back to CSA heuristics.',
      );
    } catch (e) {
      console.error('save defaultTriageAgentId:', e);
      setError(e.message || 'Could not update default triage agent.');
    } finally {
      setSavingTriageAgent(false);
    }
  };

  if (!isCompanyAdmin) {
    return (
      <Alert severity="warning">
        Only Company Admins can edit company information.
      </Alert>
    );
  }

  const handleSave = async () => {
    if (!resolvedCompanyId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      /** @type {Record<string, string>} */
      const profile = {};
      for (const key of COMPANY_PROFILE_FIELDS) {
        profile[key] = (form[key] || '').trim();
      }
      await updateDoc(doc(db, 'companies', resolvedCompanyId), {
        name: (form.name || '').trim(),
        profile,
        profileUpdatedAt: serverTimestamp(),
        profileUpdatedByUid: auth.currentUser?.uid || null,
      });
      setDirty(false);
      setSuccess('Company information saved.');
    } catch (e) {
      console.error('CompanyGeneralSettings save:', e);
      setError(e.message || 'Could not save company information.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <BusinessIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Company information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Core details used across your workspace, invoices, and communications.
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Company name"
                value={form.name}
                onChange={setField('name')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Legal / registered name"
                value={form.legalName}
                onChange={setField('legalName')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Website"
                placeholder="https://example.com"
                value={form.website}
                onChange={setField('website')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Tax / registration ID"
                value={form.taxId}
                onChange={setField('taxId')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Contact email"
                type="email"
                value={form.contactEmail}
                onChange={setField('contactEmail')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Contact phone"
                value={form.contactPhone}
                onChange={setField('contactPhone')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                placeholder="Short description of your company"
                value={form.description}
                onChange={setField('description')}
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <PlaceIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Address
            </Typography>
          </Stack>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Address line 1"
                value={form.addressLine1}
                onChange={setField('addressLine1')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Address line 2"
                value={form.addressLine2}
                onChange={setField('addressLine2')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="City"
                value={form.city}
                onChange={setField('city')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="State / region"
                value={form.stateRegion}
                onChange={setField('stateRegion')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Postal code"
                value={form.postalCode}
                onChange={setField('postalCode')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Country"
                value={form.country}
                onChange={setField('country')}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!dirty || saving}
            >
              Save company information
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <ConfirmationNumberOutlinedIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Ticketing preferences
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Control how tickets are scoped to your Apps &amp; Sites.
              </Typography>
            </Box>
          </Stack>

          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={forceTicketApp}
                onChange={handleToggleForceTicketApp}
                disabled={savingTicketPref}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Enforce App Selection on Tickets
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  When enabled, users must select an App/Site when creating a new ticket. When
                  disabled, tickets can be created as global workspace items.
                </Typography>
              </Box>
            }
          />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <SupportAgentIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Global Support Widget
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select the agent that will receive all L1 triage tickets from the
                global reporting widget.
              </Typography>
            </Box>
          </Stack>

          {rosterLoading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading AI worker roster…
              </Typography>
            </Stack>
          ) : (
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel id="default-triage-agent-label">
                Default Triage Agent
              </InputLabel>
              <Select
                labelId="default-triage-agent-label"
                label="Default Triage Agent"
                value={defaultTriageAgentId}
                onChange={(e) => {
                  setDefaultTriageAgentId(e.target.value);
                  setTriageDirty(true);
                  setSuccess('');
                }}
                disabled={savingTriageAgent}
              >
                <MenuItem value="">
                  <em>None (use CSA heuristics)</em>
                </MenuItem>
                {activeRoster.map((agent) => {
                  const name =
                    agent.name || agent.agentName || agent.id || 'Unnamed agent';
                  const role = agent.roleTitle || agent.role || agent.rosterRole;
                  return (
                    <MenuItem key={agent.id} value={agent.id}>
                      {role ? `${name} · ${role}` : name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}

          {!rosterLoading && activeRoster.length === 0 ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No active AI workers found. Seed agents in AI Workforce Roster
              first.
            </Alert>
          ) : null}

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={
                savingTriageAgent ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSaveTriageAgent}
              disabled={
                rosterLoading || savingTriageAgent || !triageDirty
              }
            >
              Save support widget settings
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
}
